// ---------------------------------------------------------------------------
// Brands Service - V11 Discovery API
// ---------------------------------------------------------------------------
// API client for V11 Discovery Brands endpoints
// ---------------------------------------------------------------------------

import { api } from '@/lib/api'

export interface BrandsFilters {
  readonly page?: number
  readonly limit?: number
  readonly sort?: string
  readonly order?: 'asc' | 'desc'
  readonly search?: string
  readonly category?: readonly number[]
  readonly subcategory?: readonly number[]
}

export interface BrandsResponse {
  readonly success: boolean
  readonly data: readonly Record<string, unknown>[]
  readonly meta: {
    readonly page: number
    readonly limit: number
    readonly total: number
    readonly lastPage: number
    readonly hasMorePages: boolean
  }
}

export interface BrandsAggregationsResponse {
  readonly success: boolean
  readonly data: {
    readonly categories: readonly { name: string; count: number; mentions: number }[]
    readonly subcategories: readonly { name: string; count: number; mentions: number }[]
    readonly topBrands: readonly { name: string; count: number }[]
    readonly mentionTiers: readonly { name: string; count: number }[]
    readonly profileMentionTiers: readonly { name: string; count: number }[]
    readonly profileTiers: readonly { name: string; count: number }[]
    readonly totals: {
      readonly brands: number
      readonly profileDetections: number
      readonly postDetections: number
      readonly totalDetections: number
      readonly profiles: number
    }
  }
}

export interface BrandsTrendsResponse {
  readonly success: boolean
  readonly data: {
    readonly metric: string
    readonly groupBy: string
    readonly timeline: readonly Record<string, unknown>[]
  }
}

export const brandsService = {
  async getBrands(filters: BrandsFilters = {}): Promise<BrandsResponse> {
    const response = await api.get<BrandsResponse>('/api/v11/discovery/brands', {
      params: filters,
    })
    return response
  },

  async getAggregations(filters: BrandsFilters = {}): Promise<BrandsAggregationsResponse> {
    const response = await api.get<BrandsAggregationsResponse>(
      '/api/v11/discovery/brands/aggregations',
      { params: filters }
    )
    return response
  },

  async getTrends(
    metric: string,
    groupBy: string,
    filters: BrandsFilters = {}
  ): Promise<BrandsTrendsResponse> {
    const response = await api.get<BrandsTrendsResponse>(
      '/api/v11/discovery/brands/trends',
      { params: { metric, group_by: groupBy, ...filters } }
    )
    return response
  },
}
